import { useState, useEffect } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { X, Send, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const firstOfMonthISO = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Generic bulk-send modal used for:
 *   - Customer statements   (kind="customer", segment="customers/statements")
 *   - Vendor statements     (kind="vendor", segment="vendors/statements")
 *   - Invoice reminders     (kind="reminder", segment="invoices/reminders")
 *
 * Props:
 *   open, onClose, bookId, kind, lang, onSent
 */
const BulkSendModal = ({ open, onClose, bookId, kind, lang, onSent }) => {
  const [start, setStart] = useState(firstOfMonthISO());
  const [end, setEnd] = useState(todayISO());
  const [onlyWithBalance, setOnlyWithBalance] = useState(true);
  const [onlyOverdue, setOnlyOverdue] = useState(true);
  const [message, setMessage] = useState("");
  const [plan, setPlan] = useState(null);
  const [exclude, setExclude] = useState(() => new Set());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const segment = kind === "customer" ? "customers/statements" : kind === "vendor" ? "vendors/statements" : "invoices/reminders";
  const labels = {
    customer: { title_en: "Bulk Send Customer Statements", title_es: "Envío Masivo · Estados de Cuenta (Clientes)", who_en: "Customer", who_es: "Cliente", filter_en: `Only customers with open balance`, filter_es: `Solo clientes con saldo`, amountKey: "closing_balance", amountLabel_en: "Balance", amountLabel_es: "Saldo" },
    vendor:   { title_en: "Bulk Send Vendor Statements",   title_es: "Envío Masivo · Estados de Cuenta (Proveedores)", who_en: "Vendor", who_es: "Proveedor", filter_en: `Only vendors with open balance`, filter_es: `Solo proveedores con saldo`, amountKey: "closing_balance", amountLabel_en: "Balance", amountLabel_es: "Saldo" },
    reminder: { title_en: "Bulk Send Invoice Reminders",   title_es: "Envío Masivo · Recordatorios de Facturas",      who_en: "Invoice",  who_es: "Factura",     filter_en: `Only overdue invoices`,           filter_es: `Solo facturas vencidas`,           amountKey: "balance",          amountLabel_en: "Balance", amountLabel_es: "Saldo" },
  }[kind];

  const buildBody = (dryRun, include_ids) => {
    if (kind === "reminder") return { only_overdue: onlyOverdue, message: message || undefined, include_ids, dry_run: dryRun };
    return { start, end, only_with_balance: onlyWithBalance, only_with_activity: !onlyWithBalance ? false : true, message: message || undefined, include_ids, dry_run: dryRun };
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/${segment}/bulk-send`, buildBody(true));
      setPlan(data.plan || []);
      setExclude(new Set());
    } catch (err) { toast.error("Could not load plan"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setMessage(""); setResult(null); setPlan(null); setExclude(new Set());
    refresh();
    // eslint-disable-next-line
  }, [open, kind]);

  if (!open) return null;

  const willSend = (plan || []).filter((r) => !exclude.has(r.id));
  const toggle = (id) => setExclude((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async (e) => {
    e.preventDefault();
    if (willSend.length === 0) { toast.error(lang === "es" ? "Nadie a quien enviar" : "Nothing to send"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/${segment}/bulk-send`, buildBody(false, willSend.map((r) => r.id)));
      setResult(data);
      const note = data.mocked ? (lang === "es" ? " (modo prueba — sin clave Resend)" : " (MOCKED — no Resend key configured)") : "";
      toast.success((lang === "es" ? `${data.sent_count} enviados` : `${data.sent_count} sent`) + note);
      onSent?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={`bulk-${kind}-modal`}>
      <div className="bg-white max-w-3xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div>
            <div className="rn3-eyebrow">{lang === "es" ? "Envío Masivo" : "Bulk Send"}</div>
            <h3 className="font-serif-display text-2xl mt-1">{lang === "es" ? labels.title_es : labels.title_en}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4" data-testid={`bulk-${kind}-result`}>
            <div className="border border-[#15803D] bg-[#F0FDF4] px-4 py-3 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#15803D] mt-0.5" />
              <div>
                <div className="font-medium">{lang === "es" ? `${result.sent_count} enviados` : `${result.sent_count} sent`}</div>
                {result.mocked && <div className="text-xs text-[#737373] mt-1">{lang === "es" ? "Modo prueba — agrega RESEND_API_KEY para envíos reales." : "MOCKED — add RESEND_API_KEY to enable real sends."}</div>}
              </div>
            </div>
            {result.skipped_count > 0 && (
              <div className="border border-[#E5E5E5] bg-[#FDFBF7] px-4 py-3">
                <div className="rn3-eyebrow mb-2">{lang === "es" ? `${result.skipped_count} omitidos` : `${result.skipped_count} skipped`}</div>
                <ul className="space-y-1 text-sm max-h-60 overflow-y-auto">
                  {result.skipped.map((s, i) => (
                    <li key={`${s.id}-${i}`} className="flex justify-between"><span>{s.name || s.invoice_number}</span><span className="text-xs uppercase tracking-widest text-[#737373]">{s.reason.replace(/_/g, " ")}</span></li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={onClose} className="rn3-btn-primary" data-testid={`bulk-${kind}-close`}>{lang === "es" ? "Cerrar" : "Close"}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              {kind !== "reminder" ? (
                <>
                  <div>
                    <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Desde" : "Start"}</label>
                    <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rn3-input" data-testid={`bulk-${kind}-start`} />
                  </div>
                  <div>
                    <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Hasta" : "End"}</label>
                    <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rn3-input" data-testid={`bulk-${kind}-end`} />
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#737373]">
                    <input type="checkbox" checked={onlyWithBalance} onChange={(e) => setOnlyWithBalance(e.target.checked)} data-testid={`bulk-${kind}-only-balance`} />
                    {lang === "es" ? labels.filter_es : labels.filter_en}
                  </label>
                </>
              ) : (
                <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#737373]">
                  <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} data-testid={`bulk-${kind}-only-overdue`} />
                  {lang === "es" ? labels.filter_es : labels.filter_en}
                </label>
              )}
              <button type="button" onClick={refresh} className="rn3-btn-gold ml-auto" data-testid={`bulk-${kind}-refresh`}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {lang === "es" ? "Actualizar" : "Refresh"}
              </button>
            </div>

            {plan && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Elegibles" : "Eligible"}</div><div className="font-serif-display text-2xl mt-1">{plan.length}</div></div>
                <div className="border border-[#15803D] p-3 bg-[#F0FDF4]"><div className="rn3-eyebrow">{lang === "es" ? "Se enviarán" : "Will send"}</div><div className="font-serif-display text-2xl mt-1 text-[#15803D]">{willSend.length}</div></div>
                <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Excluidos" : "Excluded"}</div><div className="font-serif-display text-2xl mt-1 text-[#737373]">{exclude.size}</div></div>
              </div>
            )}

            {plan === null && loading ? (
              <div className="py-10 text-center text-[#737373]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
            ) : plan && plan.length === 0 ? (
              <div className="border border-[#E5E5E5] bg-[#FDFBF7] px-4 py-6 text-center text-sm text-[#737373] flex items-center justify-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#C4A252]" />
                {lang === "es" ? "Ningún destinatario coincide con los filtros." : "No recipients match the current filters."}
              </div>
            ) : plan && (
              <div className="border border-[#E5E5E5] max-h-72 overflow-y-auto">
                <table className="w-full text-sm" data-testid={`bulk-${kind}-list`}>
                  <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7] sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-8"></th>
                      <th className="py-2 px-3">{lang === "es" ? labels.who_es : labels.who_en}</th>
                      <th className="py-2 px-3">Email</th>
                      {kind === "reminder" && <th className="py-2 px-3 text-right">{lang === "es" ? "Vencido" : "Days"}</th>}
                      <th className="py-2 px-3 text-right">{lang === "es" ? labels.amountLabel_es : labels.amountLabel_en}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.map((r) => {
                      const excluded = exclude.has(r.id);
                      const overdue = (r.days_overdue || 0) > 0;
                      return (
                        <tr key={r.id} className="border-b border-[#F3EFE9]" data-testid={`bulk-${kind}-row-${r.id}`}>
                          <td className="py-2 px-3">
                            <input type="checkbox" checked={!excluded} onChange={() => toggle(r.id)} data-testid={`bulk-${kind}-toggle-${r.id}`} />
                          </td>
                          <td className="py-2 px-3 font-medium">{r.name || r.invoice_number}{r.customer ? <span className="text-xs text-[#737373] ml-2">→ {r.customer}</span> : null}</td>
                          <td className="py-2 px-3 text-xs text-[#737373]">{r.to}</td>
                          {kind === "reminder" && <td className={`py-2 px-3 text-right tabular-nums text-xs ${overdue ? "text-[#8B1C22] font-medium" : "text-[#737373]"}`}>{r.days_overdue}</td>}
                          <td className={`py-2 px-3 text-right tabular-nums ${r[labels.amountKey] > 0 ? "text-[#8B1C22]" : "text-[#737373]"}`}>{fmt(r[labels.amountKey])}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Mensaje opcional para todos" : "Optional message for all recipients"}</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="rn3-input" data-testid={`bulk-${kind}-message`} placeholder={lang === "es" ? "Se incluirá en cada email…" : "Will be included in every email…"} />
            </div>
            <div className="text-xs text-[#737373] border border-[#F3EFE9] bg-[#FDFBF7] px-3 py-2">
              {lang === "es" ? "Cada destinatario recibe su propio PDF con la marca de tu firma. Las casillas desmarcadas no se enviarán." : "Each recipient receives their own branded PDF. Unchecked rows are skipped."}
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E5E5E5]">
              <span className="text-xs text-[#737373]">{willSend.length} {lang === "es" ? "destinatarios" : "recipients"}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] uppercase tracking-widest">{lang === "es" ? "Cancelar" : "Cancel"}</button>
                <button disabled={busy || willSend.length === 0} className="rn3-btn-primary" data-testid={`bulk-${kind}-submit`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "…" : (lang === "es" ? `Enviar ${willSend.length}` : `Send ${willSend.length}`)}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BulkSendModal;
